import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ShieldAlert, Shield, UserPlus, X } from "lucide-react";
import { ArrowLeft, Calendar, MapPin, Users, Bike, Footprints, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { EventStatusBadge, type EstadoConfirmacion } from "@/components/event-status-badge";
import { ShareEventButton } from "@/components/share-event-button";
import { JoinConsentDialog } from "@/components/join-consent-dialog";
import {
  getEventSweepers,
  getEligibleSweeperCandidates,
  addEventSweeper,
  removeEventSweeper,
} from "@/lib/event-sweepers.functions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";


type EventRecord = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  distance_km: number | null;
  meeting_point: string;
  meeting_details: string | null;
  description: string | null;
  cafe_id: string | null;
  organizador_tipo: "club" | "cafeteria";
  tipo_actividad?: "running" | "ciclismo" | "trail" | "caminata" | "otro" | "ambos" | "social" | null;
  cupo_maximo: number | null;
  estado_confirmacion: EstadoConfirmacion;
  cancelado?: boolean;
  created_at: string;
  organizer_id: string;
  clubs: { name: string; city: string | null; type: string } | null;
};

type Cafe = { name: string; location: string } | null;

type Inscrito = {
  usuario_id: string;
  fecha_inscripcion: string;
  full_name: string | null;
};

export function EventDetailView({ eventId }: { eventId: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<EventRecord | null>(null);
  const [cafe, setCafe] = useState<Cafe>(null);
  const [inscritos, setInscritos] = useState<Inscrito[]>([]);
  const [joined, setJoined] = useState(false);
  const [canViewSafety, setCanViewSafety] = useState(false);
  const [pendingJoin, setPendingJoin] = useState(false);

  const [sweepersOpen, setSweepersOpen] = useState(false);
  const [sweepers, setSweepers] = useState<Array<{ id: string; user_id: string; full_name: string | null }>>([]);
  const [candidates, setCandidates] = useState<Array<{ id: string; full_name: string | null }>>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null);
  const [sweepersLoading, setSweepersLoading] = useState(false);
  const [sweepersBusy, setSweepersBusy] = useState<string | null>(null);

  const fetchSweepers = useServerFn(getEventSweepers);
  const fetchCandidates = useServerFn(getEligibleSweeperCandidates);
  const callAddSweeper = useServerFn(addEventSweeper);
  const callRemoveSweeper = useServerFn(removeEventSweeper);


  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data: ev } = await supabase
        .from("events")
        .select(
          "id,name,event_date,event_time,distance_km,meeting_point,meeting_details,description,cafe_id,organizador_tipo,tipo_actividad,cupo_maximo,estado_confirmacion,created_at,organizer_id,clubs(name,city,type)",
        )
        .eq("id", eventId)
        .maybeSingle();
      if (cancelled) return;
      const evt = (ev as unknown as EventRecord) ?? null;
      setEvent(evt);

      if (evt?.cafe_id) {
        const { data: c } = await supabase
          .from("cafes")
          .select("name,location")
          .eq("id", evt.cafe_id)
          .maybeSingle();
        if (!cancelled) setCafe((c as Cafe) ?? null);
      } else {
        setCafe(null);
      }

      const { data: ins } = await supabase
        .from("inscripciones")
        .select("usuario_id,fecha_inscripcion")
        .eq("evento_id", eventId)
        .order("fecha_inscripcion");
      const rows = (ins ?? []) as { usuario_id: string; fecha_inscripcion: string }[];
      const profiles: Record<string, string | null> = {};
      if (rows.length) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("id,full_name")
          .in("id", rows.map((r) => r.usuario_id));
        (prof ?? []).forEach((p: { id: string; full_name: string | null }) => {
          profiles[p.id] = p.full_name;
        });
      }
      if (cancelled) return;
      setInscritos(
        rows.map((r) => ({
          usuario_id: r.usuario_id,
          fecha_inscripcion: r.fecha_inscripcion,
          full_name: profiles[r.usuario_id] ?? null,
        })),
      );
      if (user) setJoined(rows.some((r) => r.usuario_id === user.id));

      if (user && evt) {
        if (evt.organizer_id === user.id) {
          setCanViewSafety(true);
        } else {
          const { data: role } = await supabase
            .from("event_safety_roles")
            .select("id")
            .eq("event_id", eventId)
            .eq("user_id", user.id)
            .maybeSingle();
          if (!cancelled) setCanViewSafety(!!role);
        }
      } else {
        setCanViewSafety(false);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [eventId, user]);

  const afterJoin = async () => {
    setJoined(true);
    const { data: ins } = await supabase
      .from("inscripciones")
      .select("usuario_id,fecha_inscripcion")
      .eq("evento_id", eventId)
      .order("fecha_inscripcion");
    const rows = (ins ?? []) as { usuario_id: string; fecha_inscripcion: string }[];
    setInscritos((prev) =>
      rows.map((r) => ({
        usuario_id: r.usuario_id,
        fecha_inscripcion: r.fecha_inscripcion,
        full_name: prev.find((p) => p.usuario_id === r.usuario_id)?.full_name ?? null,
      })),
    );
  };

  const loadSweepers = async () => {
    setSweepersLoading(true);
    try {
      const [{ sweepers: sw }, { candidates: cd }] = await Promise.all([
        fetchSweepers({ data: { eventId } }),
        fetchCandidates({ data: { eventId } }),
      ]);
      setSweepers(sw);
      setCandidates(cd);
      setSelectedCandidate(null);
    } catch (e: any) {
      toast.error(e?.message || "Error al cargar barredoras");
    } finally {
      setSweepersLoading(false);
    }
  };

  const handleAddSweeper = async () => {
    if (!selectedCandidate) return;
    setSweepersBusy("add");
    try {
      await callAddSweeper({ data: { eventId, userId: selectedCandidate } });
      toast.success("Barredora asignada");
      await loadSweepers();
    } catch (e: any) {
      toast.error(e?.message || "Error al asignar barredora");
    } finally {
      setSweepersBusy(null);
    }
  };

  const handleRemoveSweeper = async (roleId: string) => {
    setSweepersBusy(roleId);
    try {
      await callRemoveSweeper({ data: { roleId } });
      toast.success("Barredora removida");
      await loadSweepers();
    } catch (e: any) {
      toast.error(e?.message || "Error al remover barredora");
    } finally {
      setSweepersBusy(null);
    }
  };


  const isOrganizer = useMemo(() => !!user && !!event && event.organizer_id === user.id, [user, event]);

  useEffect(() => {
    if (isOrganizer) {
      loadSweepers();
    }
  }, [isOrganizer, eventId]);

  if (loading) {
    return <div className="mx-auto max-w-4xl px-4 md:px-6 py-20 text-muted-foreground">Cargando...</div>;
  }

  const declined = event?.estado_confirmacion === "declinado";

  if (!event || (declined && !isOrganizer)) {
    return (
      <div className="mx-auto max-w-4xl px-4 md:px-6 py-20 space-y-4">
        <p className="text-muted-foreground">Este evento no está disponible.</p>
        <button
          onClick={() => navigate({ to: user ? "/dashboard" : "/" })}
          className="text-primary hover:underline"
        >
          ← Volver
        </button>
      </div>
    );
  }

  const isCafeEvent = event.organizador_tipo === "cafeteria";
  const ta = event.tipo_actividad;
  const type = isCafeEvent
    ? (ta === "ciclismo" ? "ride" : ta === "ambos" ? "both" : ta === "social" ? "social" : "run")
    : event.clubs?.type ?? "run";
  const typeLabel =
    type === "social"
      ? "Social"
      : type === "bike" || type === "ride"
        ? "Ciclismo"
        : type === "both"
          ? "Ambos"
          : "Running";
  const TypeIcon = type === "bike" || type === "ride" ? Bike : Footprints;
  const atCupo = event.cupo_maximo !== null && inscritos.length >= event.cupo_maximo;

  const visible = inscritos.slice(0, 5);
  const extra = Math.max(0, inscritos.length - visible.length);

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-6 py-12 space-y-10">
      <Link
        to={user ? "/dashboard" : "/"}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver
      </Link>

      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs uppercase tracking-widest">
              <TypeIcon className="w-3.5 h-3.5" />
              {typeLabel}
            </div>
            {isCafeEvent && (
              <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded bg-[oklch(0.7_0.18_25)] text-white">
                Evento del local
              </span>
            )}
            {!isCafeEvent && event.cafe_id && (
              <EventStatusBadge
                estado={event.estado_confirmacion}
                createdAt={event.created_at}
                showDeclined={isOrganizer}
                size="md"
              />
            )}
          </div>
          <ShareEventButton
            variant="full"
            event={{
              id: event.id,
              name: event.name,
              event_date: event.event_date,
              event_time: event.event_time,
              meeting_point: event.meeting_point,
              cafe_name: cafe?.name ?? null,
              cafe_location: cafe?.location ?? null,
              distance_km: event.distance_km,
              club_name: event.clubs?.name ?? null,
            }}
          />
        </div>
        {event.clubs?.name && (
          <p className="text-primary text-xs uppercase tracking-[0.3em]">{event.clubs.name}</p>
        )}
        {isCafeEvent && cafe?.name && (
          <p className="text-primary text-xs uppercase tracking-[0.3em]">organizado por {cafe.name}</p>
        )}
        <h1 className="font-display text-4xl sm:text-5xl break-words">{event.name}</h1>
        {event.clubs?.city && <p className="text-muted-foreground">{event.clubs.city}</p>}
      </div>

      {canViewSafety && (
        <Link
          to="/evento/$eventId/seguridad"
          params={{ eventId: event.id }}
          className="inline-flex items-center gap-2 px-5 py-3 min-h-12 rounded-md border-2 border-primary bg-primary/10 text-primary font-semibold hover:bg-primary/20"
        >
          <ShieldAlert className="w-5 h-5" /> Roster de emergencia
        </Link>
      )}

      {isOrganizer && (
        <section className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="mt-1 p-2 rounded-full bg-primary/10 text-primary">
              <Shield className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl">Barredoras de la rodada</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Las barredoras pueden ver el roster de emergencia durante el evento para ayudar si
                alguien tiene un percance.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {sweepers.length === 0
                ? "Aún no has asignado barredoras."
                : sweepers.length === 1
                  ? "1 barredora asignada."
                  : `${sweepers.length} barredoras asignadas.`}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSweepersOpen(true);
                loadSweepers();
              }}
            >
              Gestionar barredoras
            </Button>
          </div>
        </section>
      )}

      <Dialog open={sweepersOpen} onOpenChange={setSweepersOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Gestionar barredoras</DialogTitle>
            <DialogDescription>
              Elige entre los atletas inscritos a esta rodada. Las barredoras podrán ver el roster
              de emergencia durante el evento.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {sweepersLoading ? (
              <p className="text-sm text-muted-foreground">Cargando…</p>
            ) : (
              <>
                <div className="space-y-3">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
                    Barredoras actuales
                  </h3>
                  {sweepers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No hay barredoras asignadas.</p>
                  ) : (
                    <ul className="space-y-2">
                      {sweepers.map((s) => (
                        <li
                          key={s.id}
                          className="flex items-center justify-between gap-3 bg-muted/50 rounded-lg px-3 py-2"
                        >
                          <span className="text-sm font-medium truncate">
                            {s.full_name || "Deportista"}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={sweepersBusy === s.id}
                            onClick={() => handleRemoveSweeper(s.id)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            {sweepersBusy === s.id ? (
                              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            Quitar
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="space-y-3">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
                    Agregar barredora
                  </h3>
                  {candidates.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No hay atletas inscritos disponibles. Recuerda que el organizador y las
                      barredoras actuales no pueden ser seleccionadas.
                    </p>
                  ) : (
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Select
                          value={selectedCandidate ?? undefined}
                          onValueChange={setSelectedCandidate}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona un atleta inscrito" />
                          </SelectTrigger>
                          <SelectContent>
                            {candidates.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.full_name || "Deportista"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        onClick={handleAddSweeper}
                        disabled={!selectedCandidate || sweepersBusy === "add"}
                      >
                        {sweepersBusy === "add" ? (
                          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4" />
                        )}
                        Agregar
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid md:grid-cols-2 gap-4">
        <InfoCard icon={<Calendar className="w-5 h-5 text-primary" />} title="Fecha y hora">
          <p>
            {new Date(event.event_date).toLocaleDateString("es-MX", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
          <p className="text-muted-foreground">{event.event_time?.slice(0, 5)} hrs</p>
          {event.distance_km && (
            <p className="text-muted-foreground mt-1">{event.distance_km} km</p>
          )}
        </InfoCard>
        <InfoCard icon={<MapPin className="w-5 h-5 text-primary" />} title="Punto de reunión">
          <p>{cafe?.name ?? event.meeting_point}</p>
          {cafe?.location && <p className="text-muted-foreground">{cafe.location}</p>}
          {event.meeting_details && (
            <p className="text-sm text-muted-foreground mt-2 italic">{event.meeting_details}</p>
          )}
        </InfoCard>
      </div>

      {event.description && (
        <section>
          <h2 className="font-display text-2xl mb-3">Notas del organizador</h2>
          <div className="bg-card border border-border rounded-lg p-6 text-muted-foreground whitespace-pre-line">
            {event.description}
          </div>
        </section>
      )}

      <section className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-6 h-6 text-primary" />
          <h2 className="font-display text-2xl">
            {inscritos.length} {inscritos.length === 1 ? "persona va" : "personas van"}
            {event.cupo_maximo && (
              <span className="text-muted-foreground text-base ml-2">/ {event.cupo_maximo} cupo</span>
            )}
          </h2>
        </div>
        {inscritos.length > 0 && (
          <ul className="space-y-1.5 text-sm text-muted-foreground mb-4">
            {visible.map((i) => (
              <li key={i.usuario_id}>{i.full_name || "Deportista"}</li>
            ))}
            {extra > 0 && <li className="text-primary">y {extra} más</li>}
          </ul>
        )}
        {!user ? (
          <Link
            to="/auth"
            className="px-5 py-3 min-h-12 rounded-md bg-primary text-primary-foreground font-semibold hover:opacity-90 inline-flex items-center"
          >
            Únete a Brulo
          </Link>
        ) : joined ? (
          <button
            disabled
            className="px-5 py-3 min-h-12 rounded-md bg-primary/20 text-primary font-semibold inline-flex items-center gap-2"
          >
            <Check className="w-4 h-4" /> Inscrito
          </button>
        ) : atCupo ? (
          <button
            disabled
            className="px-5 py-3 min-h-12 rounded-md bg-muted text-muted-foreground font-semibold inline-flex items-center"
          >
            Cupo lleno
          </button>
        ) : (
          <button
            onClick={() => setPendingJoin(true)}
            className="px-5 py-3 min-h-12 rounded-md bg-primary text-primary-foreground font-semibold hover:opacity-90"
          >
            Unirme
          </button>
        )}
      </section>

      {user ? (
        <JoinConsentDialog
          eventId={pendingJoin ? eventId : null}
          eventName={event.name}
          userId={user.id}
          onClose={() => setPendingJoin(false)}
          onJoined={afterJoin}
        />
      ) : null}

    </div>
  );
}

function InfoCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-display text-lg">{title}</h3>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export function buildEventMeta(
  m:
    | null
    | {
        name: string;
        event_date: string;
        event_time: string;
        meeting_point: string;
        distance_km: number | null;
        description: string | null;
        clubs: { name: string; city: string | null; type: string } | null;
      },
  url: string,
) {
  if (!m) {
    return {
      meta: [
        { title: "Evento — Brulo" },
        {
          name: "description",
          content: "Detalle de la salida en Brulo: fecha, punto de reunión y cafetería anfitriona.",
        },
        { property: "og:title", content: "Evento — Brulo" },
        { property: "og:description", content: "Detalle de la salida en Brulo." },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
    };
  }
  const club = m.clubs?.name ? ` · ${m.clubs.name}` : "";
  const title = `${m.name}${club} — Brulo`.slice(0, 70);
  const dist = m.distance_km ? ` · ${m.distance_km} km` : "";
  const dateStr = new Date(`${m.event_date}T00:00:00`).toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const time = m.event_time?.slice(0, 5) ?? "";
  const desc = (
    m.description?.trim() ||
    `${dateStr} a las ${time}${dist}. Punto de reunión: ${m.meeting_point}. Únete por Brulo.`
  ).slice(0, 160);
  return {
    meta: [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          name: m.name,
          startDate: `${m.event_date}T${m.event_time}`,
          location: {
            "@type": "Place",
            name: m.meeting_point,
            address: m.clubs?.city ?? undefined,
          },
          organizer: m.clubs?.name
            ? { "@type": "Organization", name: m.clubs.name }
            : undefined,
          description: desc,
          url,
        }),
      },
    ],
  };
}

export async function loadEventMeta(eventId: string) {
  const { data } = await supabase
    .from("events")
    .select("name,event_date,event_time,meeting_point,distance_km,description,clubs(name,city,type)")
    .eq("id", eventId)
    .maybeSingle();
  return data as null | {
    name: string;
    event_date: string;
    event_time: string;
    meeting_point: string;
    distance_km: number | null;
    description: string | null;
    clubs: { name: string; city: string | null; type: string } | null;
  };
}
